(function () {
  let photoSwipeAssets;
  const sectionDataCache = new WeakMap();
  const productRequests = new Map();
  const productOptionCache = new WeakMap();
  const availableButtonContent = new WeakMap();

  function getSectionData(element) {
    const section = element.closest('.shopify-section') || document;
    const script = section.querySelector('[data-section-type="static-product"][data-section-data]');
    if (!script) return null;
    if (sectionDataCache.has(script)) return sectionDataCache.get(script);

    try {
      const data = JSON.parse(script.textContent);
      sectionDataCache.set(script, data);
      return data;
    } catch (error) {
      sectionDataCache.set(script, null);
      return null;
    }
  }

  function getProduct(element) {
    const sectionData = getSectionData(element);
    if (sectionData?.product) return Promise.resolve(sectionData.product);

    const productUrl = element.getAttribute('product-url');
    if (!productUrl) return Promise.resolve(null);
    if (!productRequests.has(productUrl)) {
      productRequests.set(productUrl, fetch(productUrl)
        .then((response) => {
          if (!response.ok) throw new Error(`Unable to load product: ${response.status}`);
          return response.json();
        })
        .catch(() => {
          productRequests.delete(productUrl);
          return null;
        }));
    }
    return productRequests.get(productUrl);
  }

  function getOptionControls(optionElements) {
    const select = [];
    const radio = [];

    optionElements.forEach((optionElement) => {
      const wrappers = optionElement.matches('[data-variant-option-value-wrapper]')
        ? [optionElement]
        : Array.from(optionElement.querySelectorAll('[data-variant-option-value-wrapper]'));
      const values = optionElement.matches('[data-variant-option-value]')
        ? [optionElement]
        : Array.from(optionElement.querySelectorAll('[data-variant-option-value]'));
      if (!values.length) return;

      const option = { option: optionElement, wrappers, values };
      if (values[0].matches('option')) select.push(option);
      else if (values[0].matches('input[type="radio"]')) radio.push(option);
    });

    return { select, radio };
  }

  function getSelectedOptions(product, selectOptions, radioOptions) {
    const options = product.options.map(() => 'not-selected');

    selectOptions.forEach(({ option }) => {
      if (option.value !== 'not-selected') {
        options[Number(option.dataset.variantOptionIndex)] = option.value;
      }
    });
    radioOptions.forEach(({ values }) => {
      values.forEach((value) => {
        if (value.checked) options[Number(value.dataset.variantOptionValueIndex)] = value.value;
      });
    });

    return options;
  }

  function findVariant(variants, selectedOptions) {
    return variants.find((variant) => variant.options.every((option, index) => option === selectedOptions[index])) || null;
  }

  function setSelectedOptions(selectOptions, radioOptions, selectedOptions) {
    selectOptions.forEach(({ option }) => {
      option.value = selectedOptions[Number(option.dataset.variantOptionIndex)];
    });
    radioOptions.forEach(({ values }) => {
      values.forEach((value) => {
        value.checked = value.value === selectedOptions[Number(value.dataset.variantOptionValueIndex)];
      });
    });
  }

  function isVariantAvailable(variant) {
    const stockEntry = window.TMG_STOCK?.variants?.[String(variant?.id)];
    return Boolean(variant?.available && stockEntry?.a !== false);
  }

  function getOptionsAccessibility(product, selectedOptions) {
    if (!productOptionCache.has(product)) {
      const baseAccessibility = product.options.map(() => Object.create(null));
      product.variants.forEach((variant) => {
        variant.options.forEach((value, optionIndex) => {
          baseAccessibility[optionIndex][value] = Boolean(
            baseAccessibility[optionIndex][value] || isVariantAvailable(variant)
          );
        });
      });

      productOptionCache.set(product, {
        availableVariants: product.variants.filter(isVariantAvailable),
        variantsByOptions: new Map(
          product.variants.map((variant) => [JSON.stringify(variant.options), variant])
        ),
        baseAccessibility,
      });
    }

    const { availableVariants, variantsByOptions, baseAccessibility } = productOptionCache.get(product);
    const accessibility = baseAccessibility.map((optionValues) => {
      const states = Object.create(null);
      Object.keys(optionValues).forEach((value) => {
        states[value] = { accessible: optionValues[value], available: false };
      });
      return states;
    });

    accessibility.forEach((optionValues, optionIndex) => {
      Object.keys(optionValues).forEach((value) => {
        const candidateOptions = selectedOptions.slice();
        candidateOptions[optionIndex] = value;
        const hasUnselectedOption = candidateOptions.includes('not-selected');
        const candidate = hasUnselectedOption
          ? null
          : variantsByOptions.get(JSON.stringify(candidateOptions));
        optionValues[value].available = hasUnselectedOption
          ? availableVariants.some((variant) => candidateOptions.every((option, index) => (
            option === 'not-selected' || variant.options[index] === option
          )))
          : isVariantAvailable(candidate);
      });
    });

    return accessibility;
  }

  function updateOptions(product, selectOptions, radioOptions, selectedOptions, disableUnavailable, removeUnavailable) {
    const accessibility = getOptionsAccessibility(product, selectedOptions);

    [...selectOptions, ...radioOptions].forEach(({ option, wrappers, values }) => {
      const optionIndex = Number(option.dataset.variantOptionIndex);
      const fragment = document.createDocumentFragment();

      values.forEach((value, index) => {
        const state = accessibility[optionIndex]?.[value.value] || { accessible: false, available: false };
        const isChooseOption = value.value === 'not-selected';
        value.disabled = isChooseOption || (disableUnavailable && !state.accessible);
        value.dataset.variantOptionAccessible = state.accessible;
        value.dataset.variantOptionAvailable = state.available;

        const wrapper = wrappers[index];
        wrapper?.classList.toggle('options-selection__option-value--selected', Boolean(value.selected || value.checked));
        if (wrapper && (!removeUnavailable || state.accessible || isChooseOption)) fragment.appendChild(wrapper);
      });

      option.replaceChildren(fragment);
      const chosenValue = values.find((value) => value.selected || value.checked);
      option.dataset.variantOptionChosenValue = chosenValue && chosenValue.value !== 'not-selected'
        ? chosenValue.value
        : false;
    });
  }

  class VariantSelection extends HTMLElement {
    static get observedAttributes() {
      return ['variant'];
    }

    constructor() {
      super();
      this.dispatchVariantChange = true;
      this.onMainChange = (event) => { this.variant = event.currentTarget.value; };
    }

    connectedCallback() {
      this.mainInput = this.querySelector('[data-variants]');
      if (!this.mainInput || !this.querySelector('options-selection')) return;
      this.mainInput.addEventListener('change', this.onMainChange);
    }

    disconnectedCallback() {
      this.mainInput?.removeEventListener('change', this.onMainChange);
    }

    attributeChangedCallback(name, oldValue, newValue) {
      if (name !== 'variant' || oldValue === newValue || !this.mainInput) return;
      const dispatchEvent = this.dispatchVariantChange;
      this.dispatchVariantChange = true;
      this.changeVariant(newValue, dispatchEvent);
    }

    set variant(value) {
      this.setVariant(value);
    }

    get variant() {
      return this.getAttribute('variant');
    }

    getProduct() {
      return getProduct(this);
    }

    getVariant() {
      return this.getProduct().then((product) => (
        product?.variants.find((variant) => String(variant.id) === this.variant) || false
      ));
    }

    getState() {
      return this.getVariant().then((variant) => variant ? 'selected' : this.variant);
    }

    setVariant(value, dispatchEvent = true) {
      if (value === this.variant) {
        if (this.mainInput) this.mainInput.value = value;
        return;
      }

      this.dispatchVariantChange = dispatchEvent;
      if (value) this.setAttribute('variant', value);
      else this.removeAttribute('variant');
    }

    changeVariant(value, dispatchEvent = true) {
      if (this.mainInput) this.mainInput.value = value;
      if (!dispatchEvent) return;
      this.getProduct().then((product) => {
        if (!this.isConnected || this.variant !== value) return;
        const variant = product?.variants.find((item) => String(item.id) === value) || false;
        this.dispatchEvent(new CustomEvent('variant-change', {
          bubbles: true,
          detail: { product, variant, state: variant ? 'selected' : value },
        }));
      });
    }
  }

  class OptionsSelection extends HTMLElement {
    constructor() {
      super();
      this.onOptionChange = this.onOptionChange.bind(this);
      this.events = [];
    }

    connectedCallback() {
      this.style.display = '';
      this.variantSelection = this.getAttribute('variant-selection')
        ? document.getElementById(this.getAttribute('variant-selection'))
        : this.closest('variant-selection');
      this.optionElements = Array.from(this.querySelectorAll('[data-variant-option]'));
      ({ select: this.selectOptions, radio: this.radioOptions } = getOptionControls(this.optionElements));

      this.selectOptions.forEach(({ option }) => this.addChangeEvent(option));
      this.radioOptions.forEach(({ values }) => values.forEach((value) => this.addChangeEvent(value)));
      this.onOptionChange();
    }

    disconnectedCallback() {
      this.events.forEach((element) => element.removeEventListener('change', this.onOptionChange));
      this.events = [];
    }

    addChangeEvent(element) {
      element.addEventListener('change', this.onOptionChange);
      this.events.push(element);
    }

    getVariantSelection() {
      return this.variantSelection;
    }

    getSelectedOptions() {
      if (!this.variantSelection) return Promise.resolve(null);
      return this.variantSelection.getProduct().then((product) => (
        product ? getSelectedOptions(product, this.selectOptions, this.radioOptions) : null
      ));
    }

    setSelectedOptions(selectedOptions, product = null) {
      const selectChanged = this.selectOptions.some(({ option }) => (
        option.value !== selectedOptions[Number(option.dataset.variantOptionIndex)]
      ));
      const radioChanged = this.radioOptions.some(({ values }) => values.some((value) => (
        value.checked !== (value.value === selectedOptions[Number(value.dataset.variantOptionValueIndex)])
      )));
      if (!selectChanged && !radioChanged) return Promise.resolve(false);

      setSelectedOptions(this.selectOptions, this.radioOptions, selectedOptions);
      return this.updateOptions(selectedOptions, product);
    }

    updateLabels() {
      this.optionElements.forEach((optionElement) => {
        const label = optionElement.closest('fieldset, .options-selection__select-wrapper')
          ?.querySelector('[data-variant-option-name]');
        if (!label) return;

        const chosenValue = optionElement.dataset.variantOptionChosenValue;
        label.dataset.variantOptionChosenValue = chosenValue;
        if (chosenValue !== 'false') {
          label.innerHTML = label.dataset.variantOptionName;
          const value = label.querySelector('span');
          if (value) value.textContent = chosenValue;
        } else {
          label.innerHTML = label.dataset.variantOptionChooseName;
        }
      });
    }

    applyOptions(product, selectedOptions) {
      updateOptions(
        product,
        this.selectOptions,
        this.radioOptions,
        selectedOptions || getSelectedOptions(product, this.selectOptions, this.radioOptions),
        this.hasAttribute('disable-unavailable'),
        this.hasAttribute('remove-unavailable')
      );
      this.updateLabels();
      return true;
    }

    updateOptions(selectedOptions, product = null) {
      if (!this.variantSelection) return Promise.resolve(false);
      if (product) return Promise.resolve(this.applyOptions(product, selectedOptions));
      return this.variantSelection.getProduct().then((product) => {
        if (!product) return false;
        return this.applyOptions(product, selectedOptions);
      });
    }

    synchronize(product, selectedOptions, variantId) {
      const productWrapper = this.closest('[data-product-wrapper]') || document;
      productWrapper.querySelectorAll('options-selection').forEach((selection) => {
        if (selection === this || selection.getVariantSelection?.()?.getAttribute('product-url') !== this.variantSelection.getAttribute('product-url')) return;
        selection.setSelectedOptions(selectedOptions, product);
        const variantSelection = selection.getVariantSelection();
        if (variantSelection) variantSelection.setVariant(variantId, false);
      });
    }

    onOptionChange() {
      if (!this.variantSelection) return;
      this.variantSelection.getProduct().then((product) => {
        if (!product || !this.isConnected) return;
        const selectedOptions = getSelectedOptions(product, this.selectOptions, this.radioOptions);
        const variant = findVariant(product.variants, selectedOptions);
        const variantId = variant
          ? String(variant.id)
          : selectedOptions.includes('not-selected') ? 'not-selected' : 'unavailable';

        this.applyOptions(product, selectedOptions);
        if (this.variantSelection.variant !== variantId) this.variantSelection.variant = variantId;
        this.synchronize(product, selectedOptions, variantId);
      });
    }
  }

  if (!customElements.get('variant-selection')) customElements.define('variant-selection', VariantSelection);
  if (!customElements.get('options-selection')) customElements.define('options-selection', OptionsSelection);

  function loadStylesheet(url) {
    const existing = document.querySelector('link[data-product-photoswipe-styles]');
    if (existing?.sheet) return Promise.resolve();

    return new Promise((resolve, reject) => {
      const link = existing || document.createElement('link');
      link.addEventListener('load', resolve, { once: true });
      link.addEventListener('error', (error) => {
        link.remove();
        reject(error);
      }, { once: true });
      if (!existing) {
        link.rel = 'stylesheet';
        link.href = new URL(url, window.location.href).href;
        link.dataset.productPhotoswipeStyles = 'true';
        document.head.appendChild(link);
      }
    });
  }

  function loadPhotoSwipe(gallery) {
    if (!photoSwipeAssets) {
      const moduleUrl = new URL(gallery.dataset.photoswipeModule, window.location.href).href;
      photoSwipeAssets = Promise.all([
        import(moduleUrl),
        loadStylesheet(gallery.dataset.photoswipeStyles),
      ]).then(([module]) => module.default).catch((error) => {
        photoSwipeAssets = null;
        throw error;
      });
    }
    return photoSwipeAssets;
  }

  function canOpenZoom(gallery) {
    const mode = gallery.dataset.galleryClickToZoom;
    const mobile = window.matchMedia('(max-width: 719px)').matches;
    return mode === 'always' || mode === (mobile ? 'mobile' : 'desktop');
  }

  function getPhotoSwipeData(gallery) {
    return Array.from(gallery.querySelectorAll('[data-gallery-figure][data-media-type="image"][data-zoom]'))
      .map((figure) => {
        const image = figure.querySelector('img');
        return {
          src: figure.dataset.zoom,
          srcset: figure.dataset.zoomSrcset || undefined,
          width: Number(figure.dataset.imageWidth),
          height: Number(figure.dataset.imageHeight),
          msrc: image?.currentSrc || image?.src,
          alt: image?.alt || '',
          element: figure,
          thumbCropped: gallery.hasAttribute('data-product-gallery-image-crop'),
        };
      });
  }

  function preloadSelectedImage(item) {
    const image = new Image();
    image.decoding = 'async';
    if (item.srcset) {
      image.srcset = item.srcset;
      image.sizes = '100vw';
    }
    image.src = item.src;
    return image;
  }

  async function openPhotoSwipe(gallery, trigger) {
    if (gallery.dataset.photoswipeOpening === 'true' || gallery.photoSwipe) return;

    const items = getPhotoSwipeData(gallery);
    const selectedFigure = gallery.querySelector('[data-gallery-figure][data-gallery-selected="true"][data-media-type="image"]');
    const index = items.findIndex((item) => item.element === selectedFigure);
    if (index < 0) return;

    gallery.dataset.photoswipeOpening = 'true';
    trigger.setAttribute('aria-busy', 'true');
    preloadSelectedImage(items[index]);

    try {
      const PhotoSwipe = await loadPhotoSwipe(gallery);
      const photoSwipe = new PhotoSwipe({
        dataSource: items,
        index,
        bgOpacity: 0.9,
        preload: [1, 1],
        wheelToZoom: true,
        closeTitle: gallery.dataset.photoswipeCloseTitle,
        zoomTitle: gallery.dataset.photoswipeZoomTitle,
        arrowPrevTitle: gallery.dataset.photoswipePreviousTitle,
        arrowNextTitle: gallery.dataset.photoswipeNextTitle,
      });
      photoSwipe.on('destroy', () => { gallery.photoSwipe = null; });
      gallery.photoSwipe = photoSwipe;
      photoSwipe.init();
    } finally {
      gallery.dataset.photoswipeOpening = 'false';
      trigger.removeAttribute('aria-busy');
    }
  }

  function updateZoomAccessibility(gallery) {
    const zoomEnabled = canOpenZoom(gallery);
    gallery.querySelectorAll('[data-gallery-figure][data-media-type="image"]').forEach((figure) => {
      const isInteractive = zoomEnabled && figure.dataset.gallerySelected === 'true';
      figure.tabIndex = isInteractive ? 0 : -1;
      if (isInteractive) {
        figure.setAttribute('role', 'button');
        figure.setAttribute('aria-haspopup', 'dialog');
        figure.setAttribute('aria-label', gallery.dataset.photoswipeZoomTitle);
      } else {
        figure.removeAttribute('role');
        figure.removeAttribute('aria-haspopup');
        figure.removeAttribute('aria-label');
      }
    });
  }

  function loadRimg(element) {
    if (!element || element.dataset.rimg !== 'lazy') return;

    if (element.tagName !== 'IMG') {
      const source = element.dataset.gallerySrcset?.match(/(\S+)\s+700w/)?.[1];
      if (source) element.style.backgroundImage = `url("${source}")`;
      element.dataset.rimg = 'loaded';
      return;
    }

    element.addEventListener('load', () => { element.dataset.rimg = 'loaded'; }, { once: true });
    element.addEventListener('error', () => { element.dataset.rimg = 'error'; }, { once: true });
    element.dataset.rimg = 'loading';

    if (element.dataset.gallerySrcset) {
      element.sizes = element.dataset.gallerySizes;
      element.srcset = element.dataset.gallerySrcset;
    } else {
      element.removeAttribute('srcset');
    }
  }

  /* Reached from the thumbnail click, so the user activation an unmuted play()
     needs is still available. The muted retry covers browsers that refuse
     anyway rather than leaving the viewer on a dead frame. */
  function playVideo(video) {
    const attempt = video.play();
    if (!attempt) return;
    attempt.catch(() => {
      video.muted = true;
      video.play().catch(() => {});
    });
  }

  /* YouTube (enablejsapi=1 from external_video_url) and Vimeo both accept
     postMessage commands, so neither player API script is needed. */
  function commandExternalVideo(figure, action) {
    const holder = figure.querySelector('[data-gallery-external-video]');
    const player = holder?.querySelector('iframe')?.contentWindow;
    if (!player) return;
    const message = holder.dataset.galleryExternalVideo === 'vimeo'
      ? { method: action }
      : { event: 'command', func: `${action}Video`, args: '' };
    player.postMessage(JSON.stringify(message), '*');
  }

  function playExternalVideo(figure) {
    const holder = figure.querySelector('[data-gallery-external-video]');
    const template = holder?.querySelector('template');
    if (template) holder.replaceChildren(template.content.cloneNode(true));
    else commandExternalVideo(figure, 'play');
  }

  function selectMedia(gallery, thumbnail) {
    const index = thumbnail.dataset.galleryIndex;
    const selectedFigure = gallery.querySelector(`[data-gallery-figure][data-gallery-index="${index}"]`);
    if (!selectedFigure) return;

    gallery.querySelectorAll('[data-gallery-figure]').forEach((figure) => {
      const isSelected = figure === selectedFigure;
      figure.dataset.gallerySelected = isSelected;
      figure.setAttribute('aria-hidden', String(!isSelected));
      if (isSelected) return;
      figure.querySelectorAll('video').forEach((video) => video.pause());
      commandExternalVideo(figure, 'pause');
    });

    gallery.querySelectorAll('[data-gallery-thumbnail]').forEach((item) => {
      item.dataset.gallerySelected = item === thumbnail;
    });

    selectedFigure.querySelectorAll('[data-rimg="lazy"]').forEach(loadRimg);
    updateZoomAccessibility(gallery);

    const selectedVideo = selectedFigure.querySelector('video');
    if (selectedVideo) playVideo(selectedVideo);
    playExternalVideo(selectedFigure);
  }

  function initGallery(gallery) {
    if (gallery.dataset.galleryReady === 'true') return;
    gallery.dataset.galleryReady = 'true';

    gallery.querySelectorAll('[data-gallery-navigation] img[data-rimg="lazy"]').forEach(loadRimg);
    updateZoomAccessibility(gallery);
    gallery.addEventListener('click', (event) => {
      const thumbnail = event.target.closest('[data-gallery-thumbnail]');
      if (thumbnail && gallery.contains(thumbnail)) selectMedia(gallery, thumbnail);

      const videoPlay = event.target.closest('[data-gallery-video-play]');
      if (videoPlay && gallery.contains(videoPlay)) playExternalVideo(videoPlay.closest('[data-gallery-figure]'));

      const expand = event.target.closest('[data-gallery-expand]');
      const selectedImage = event.target.closest('[data-gallery-figure][data-gallery-selected="true"][data-media-type="image"]');
      if ((expand || selectedImage) && canOpenZoom(gallery)) {
        event.preventDefault();
        const trigger = expand || selectedImage;
        openPhotoSwipe(gallery, trigger).catch(() => {});
      }
    });

    /* Touch platforms don't toggle playback when the video body is tapped, and
       the native control strip is a small target. Desktop already toggles on
       click, so this stays touch-only to avoid cancelling itself out. */
    gallery.addEventListener('pointerup', (event) => {
      if (event.pointerType !== 'touch') return;
      const video = event.target.closest('video');
      if (!video || !gallery.contains(video)) return;

      const rect = video.getBoundingClientRect();
      if (!rect.height) return;
      const offset = (event.clientY - rect.top) / rect.height;
      if (offset < 0.15 || offset > 0.75) return; // leave the control strip alone

      if (video.paused) playVideo(video);
      else video.pause();
    });

    gallery.addEventListener('keydown', (event) => {
      const selectedImage = event.target.closest('[data-gallery-figure][data-gallery-selected="true"][data-media-type="image"]');
      if (!selectedImage || !canOpenZoom(gallery) || (event.key !== 'Enter' && event.key !== ' ')) return;
      event.preventDefault();
      openPhotoSwipe(gallery, selectedImage).catch(() => {});
    });
  }

  function formatMoney(cents, format) {
    const value = Number(cents || 0);
    return String(format || '${{amount}}').replace(/\{\{\s*(\w+)\s*\}\}/g, (match, key) => {
      if (key === 'amount_no_decimals') return Math.round(value / 100).toLocaleString('en-CA');
      if (key === 'amount_with_comma_separator') {
        return (value / 100).toLocaleString('de-DE', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
      }
      if (key === 'amount_no_decimals_with_comma_separator') return Math.round(value / 100).toLocaleString('de-DE');
      if (key === 'amount_with_apostrophe_separator') {
        return (value / 100).toLocaleString('de-CH', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
      }
      return (value / 100).toLocaleString('en-CA', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
    });
  }

  function formatProductMoney(cents, format, pricing) {
    if (pricing?.dataset.priceIsFrench !== 'true') return formatMoney(cents, format);
    const amount = (Number(cents || 0) / 100)
      .toLocaleString('en-CA', { minimumFractionDigits: 2, maximumFractionDigits: 2 })
      .replace(/,/g, '\u00a0')
      .replace('.', ',');
    const symbol = pricing.dataset.priceCurrencySymbol || '$';
    return `${amount}\u00a0${symbol}${pricing.dataset.priceCurrencyCode === 'CAD' ? 'CA' : ''}`;
  }

  function updateGallery(productWrapper, variant) {
    const mediaId = variant?.featured_media?.id;
    if (!mediaId) return;

    productWrapper.querySelectorAll('[data-product-gallery]').forEach((gallery) => {
      const thumbnail = Array.from(gallery.querySelectorAll('[data-gallery-thumbnail]'))
        .find((item) => item.dataset.media === String(mediaId));
      if (thumbnail) selectMedia(gallery, thumbnail);
    });
  }

  function updatePricing(productWrapper, variant, sectionData) {
    const pricingElements = productWrapper.querySelectorAll('[data-product-pricing]');
    pricingElements.forEach((pricing) => {
      pricing.style.visibility = variant ? 'visible' : 'hidden';
    });
    if (!variant) return;

    const moneyFormat = sectionData?.settings?.money_format;
    const formatPrice = (cents) => formatProductMoney(cents, moneyFormat, pricingElements[0]);
    const price = formatPrice(variant.price);
    const compareAtPrice = Number(variant.compare_at_price || 0);
    const hasComparePrice = compareAtPrice > Number(variant.price);

    pricingElements.forEach((pricing) => {
      pricing.querySelectorAll('[data-price-container] [data-price]').forEach((element) => {
        element.textContent = price;
        element.dataset.baseCents = variant.price;
      });
      pricing.querySelectorAll('[data-price-compare-container]').forEach((container) => {
        container.classList.toggle('visible', hasComparePrice);
      });
      pricing.querySelectorAll('[data-price-compare]').forEach((element) => {
        element.textContent = hasComparePrice ? formatPrice(compareAtPrice) : '';
      });
      pricing.querySelectorAll('[data-badge-sales]').forEach((badge) => {
        badge.style.display = hasComparePrice ? '' : 'none';
      });
      pricing.querySelectorAll('[data-badge-sales-range]').forEach((badge) => {
        badge.style.display = 'none';
      });
      pricing.querySelectorAll('[data-badge-sales-single]').forEach((badge) => {
        badge.style.display = hasComparePrice ? '' : 'none';
      });
      pricing.querySelectorAll('[data-price-money-saved]').forEach((element) => {
        element.textContent = hasComparePrice ? formatPrice(compareAtPrice - variant.price) : '';
      });
      pricing.querySelectorAll('[data-price-percent-saved]').forEach((element) => {
        element.textContent = hasComparePrice ? Math.round(((compareAtPrice - variant.price) * 100) / compareAtPrice) : '';
      });

      const measurement = variant.unit_price_measurement;
      const unitPrice = pricing.querySelector('[data-unit-price]');
      if (unitPrice) unitPrice.classList.toggle('hidden', !measurement);
      if (measurement) {
        const totalQuantity = pricing.querySelector('[data-total-quantity]');
        const unitPriceAmount = pricing.querySelector('[data-unit-price-amount]');
        const unitPriceMeasure = pricing.querySelector('[data-unit-price-measure]');
        if (totalQuantity) totalQuantity.textContent = `${measurement.quantity_value}${measurement.quantity_unit}`;
        if (unitPriceAmount) unitPriceAmount.textContent = formatPrice(variant.unit_price);
        if (unitPriceMeasure) {
          unitPriceMeasure.textContent = measurement.reference_value === 1
            ? measurement.reference_unit
            : `${measurement.reference_value}${measurement.reference_unit}`;
        }
      }

      pricing.querySelectorAll('[data-tax-line]').forEach((taxLine) => {
        taxLine.classList.toggle('hidden', !variant.taxable);
      });
    });

    productWrapper.querySelectorAll('[data-tmg-total-price-wrap], .final-price').forEach((element) => {
      element.dataset.baseCents = variant.price;
    });
    productWrapper.querySelectorAll('[data-app-sticky-price]').forEach((element) => {
      element.textContent = price;
    });
  }

  function updateSku(productWrapper, variant) {
    productWrapper.querySelectorAll('span[data-product-sku]').forEach((element) => {
      element.textContent = variant?.sku || '';
    });
    productWrapper.querySelectorAll('.product-sku > span:not([data-product-sku])').forEach((element) => {
      element.textContent = variant?.sku || '';
    });
    productWrapper.querySelectorAll('[data-product-sku]:not(span)').forEach((element) => {
      element.dataset.productSku = variant?.sku || '';
    });
    productWrapper.querySelectorAll('.product-sku').forEach((element) => {
      element.classList.toggle('product-sku--empty', !variant?.sku);
    });
  }

  function updateButtons(productWrapper, variant, context) {
    productWrapper.querySelectorAll('[data-product-atc]').forEach((button) => {
      const text = button.querySelector('.atc-button--text');
      const available = isVariantAvailable(variant);

      button.disabled = !available;
      button.classList.toggle('disabled', !available);
      if (text) {
        if (available) {
          const availableHtml = availableButtonContent.get(text);
          if (availableHtml && text.innerHTML !== availableHtml) text.innerHTML = availableHtml;
          if (!availableHtml) {
            text.textContent = context?.product_available || 'Add to Cart';
            availableButtonContent.set(text, text.innerHTML);
          }
        } else {
          const label = variant ? context?.product_sold_out || 'Sold out' : context?.product_unavailable || 'Unavailable';
          if (text.textContent.trim() !== label) text.textContent = label;
        }
      }
    });
  }

  function updateStickyImage(productWrapper, variant) {
    const image = variant?.featured_image;
    if (!image?.src) return;

    const url = new URL(image.src, window.location.origin);
    url.searchParams.set('width', '100');
    productWrapper.querySelectorAll('[data-app-sticky-img]').forEach((element) => {
      element.src = url.href;
      element.alt = image.alt || element.alt;
    });
  }

  function updateSimilarProduct(productWrapper, variant) {
    const cards = [...productWrapper.querySelectorAll('[data-similar-product]')];
    if (!cards.length) return;

    const id = String(variant?.id || '');
    const active = cards.find((card) => id && card.dataset.similarProduct.split(' ').includes(id))
      || cards.find((card) => !card.dataset.similarProduct);
    cards.forEach((card) => { card.hidden = card !== active; });
  }

  function updateHistory(variant) {
    if (!variant || !window.history?.replaceState) return;
    const url = new URL(window.location.href);
    url.searchParams.set('variant', variant.id);
    window.history.replaceState({}, '', url);
  }

  function updateProduct(productWrapper, detail) {
    const sectionData = getSectionData(productWrapper);
    const { variant } = detail;

    productWrapper.querySelectorAll('[data-variants]').forEach((input) => {
      input.value = variant?.id || detail.state;
    });
    updateGallery(productWrapper, variant);
    updatePricing(productWrapper, variant, sectionData);
    updateSku(productWrapper, variant);
    updateButtons(productWrapper, variant, sectionData?.context);
    updateStickyImage(productWrapper, variant);
    updateSimilarProduct(productWrapper, variant);
    updateHistory(variant);
  }

  function initProduct(productWrapper) {
    if (productWrapper.dataset.variantUiReady === 'true' || !productWrapper.querySelector('options-selection')) return;
    productWrapper.dataset.variantUiReady = 'true';

    productWrapper.querySelectorAll('[data-product-atc]').forEach((button) => {
      const text = button.querySelector('.atc-button--text');
      if (text && !button.disabled && !button.classList.contains('disabled')) {
        availableButtonContent.set(text, text.innerHTML);
      }
    });
    productWrapper.addEventListener('variant-change', (event) => {
      if (!event.target.matches('variant-selection') || !event.detail) return;
      const variantKey = String(event.detail.variant?.id || event.detail.state || 'unavailable');
      if (productWrapper.dataset.currentVariant === variantKey) return;
      productWrapper.dataset.currentVariant = variantKey;
      updateProduct(productWrapper, event.detail);
    });
  }

  function init(root) {
    root.querySelectorAll('[data-product-gallery]').forEach(initGallery);
    root.querySelectorAll('[data-product-wrapper]').forEach(initProduct);
  }

  init(document);
  document.addEventListener('shopify:section:load', (event) => init(event.target));
})();
